// src/components/SubscriberSwitcher.js
import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box
} from '@mui/material';
import {
  Person as PersonIcon,
  Check as CheckIcon,
  DashboardCustomize as SystemAdminIcon,
  Storefront as CustomerIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { TRAK } from '../theme/trakTokens';
import { trackEvent, setAnalyticsUserContext } from '../services/analyticsService';

function SubscriberSwitcher() {
  const {
    accessibleSubscribers,
    customerAccessEntries,
    subscriberId,
    setSubscriberId,
    setCustomClaims,
    isSystemAdmin,
    userRole,
    customerInfo
  } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // The system-admin section is platform-wide, not a workspace. Entering it is
  // a plain navigate() — no claims change, no localStorage rewrite — so the
  // subscriber underneath stays selected and the app bar kept announcing it,
  // which reads as "you are still inside KNS" on a page that is nothing of the
  // sort. Say where you actually are; the workspace is still in the menu below,
  // ticked, as the thing you would return to.
  const inSystemAdmin = location.pathname.startsWith('/system-admin');

  // Get current subscriber info from localStorage
  const storedSubscriberName = localStorage.getItem('subscriberName');

  // Build combined list
  const subscribers = accessibleSubscribers || [];
  const customers = (customerAccessEntries || []).map(entry => ({
    id: entry.customerId,
    name: entry.customerName,
    role: entry.role,
    subscriberId: entry.subscriberId,
    type: 'customer'
  }));
  const totalEntries = subscribers.length + customers.length;

  // Determine current display name
  const getCurrentName = () => {
    if (userRole === 'customer' && customerInfo?.customerName) {
      return customerInfo.customerName;
    }
    if (storedSubscriberName) return storedSubscriberName;
    // localStorage is not the only source of truth for the name, and it is the
    // one that goes missing: reconcileStoredWorkspace clears `subscriberName`
    // whenever the token names a workspace the cached access list doesn't yet
    // know about, so a perfectly selected workspace rendered as "Select
    // Subscriber" while the menu below ticked it correctly. The access list
    // carries the name — read it there before giving up.
    const selected = subscribers.find((sub) => sub.id === subscriberId);
    if (selected) return selected.name || selected.subscriberCode || 'Select Subscriber';
    return 'Select Subscriber';
  };

  // Two-line workspace block from the 2026 app-bar design (handoff option
  // 6a): the workspace name reads as an identity, and the muted second line
  // says what kind of thing it is — and, when there is more than one, that it
  // can be changed.
  const workspaceLabel = (withCaret) => (
    // maxWidth + ellipsis: the app bar gives the workspace block a fixed
    // footprint so the search box next to it is the only thing that shrinks
    // when the row is tight. A long workspace name would otherwise either wrap
    // (dragging the 54px row taller) or push the whole row past the viewport.
    // 190px is half a phone, so the cap tightens below sm — the name still
    // ellipses rather than wrapping, it just stops crowding the nav row out.
    <Box sx={{ textAlign: 'left', lineHeight: 1, minWidth: 0, maxWidth: { xs: 160, sm: 190 } }}>
      <Box sx={{ font: `600 13px/1.2 ${TRAK.sans}`, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {switching ? 'Switching…' : (inSystemAdmin ? 'System Admin' : getCurrentName())}
      </Box>
      <Box sx={{ font: `400 10px/1.2 ${TRAK.sans}`, color: 'rgba(255,255,255,.55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {/* A customer portal session shows the *customer* name above, so
            labelling it "Workspace" named the wrong thing — and the job cards
            below now name the real workspace, which made the two read as a
            contradiction. Say what the name above actually is. */}
        {inSystemAdmin ? 'Platform' : (userRole === 'customer' ? 'Customer' : 'Workspace')}{withCaret ? ' ▾' : ''}
      </Box>
    </Box>
  );

  // Don't show dropdown if only one entry total
  if (totalEntries <= 1 && !isSystemAdmin) {
    return workspaceLabel(false);
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSubscriberSwitch = async (subscriber) => {
    if (subscriber.id === subscriberId && userRole !== 'customer') {
      handleMenuClose();
      // From the system-admin pages the token already carries this workspace,
      // so going back is a navigation, not a switch. This branch used to only
      // close the menu, leaving no way back short of editing the URL.
      if (inSystemAdmin) navigate('/tasks');
      return;
    }

    setSwitching(true);
    handleMenuClose();
    try {
      // Update Firebase token claims to the new subscriber (no customer_id).
      // The backend's set-custom-claims handler builds a fresh claims dict
      // without `customer_id` / `role: 'customer'`, so this clears any
      // residual customer-portal claims. Critical when switching out of
      // a customer portal — without an awaited refresh, window.location.reload()
      // below races the claims update and the route guard reads stale
      // role: 'customer' from the token, bouncing back to /customer-portal.
      // Mirrors the handleCustomerSwitch path below.
      await setCustomClaims(subscriber.id);
      const { auth: firebaseAuth } = await import('../firebase');
      if (firebaseAuth.currentUser) {
        await firebaseAuth.currentUser.getIdToken(true);
      }

      // Persist subscriber info for AppLayout / role checks after reload.
      localStorage.setItem('subscriberId', subscriber.id);
      localStorage.setItem('subscriberRole', subscriber.role);
      localStorage.setItem('subscriberCode', subscriber.subscriberCode);
      if (subscriber.name) {
        localStorage.setItem('subscriberName', subscriber.name);
      } else {
        localStorage.removeItem('subscriberName');
      }

      // Before the reload below, so the event carries the workspace it is about
      // rather than the one being left behind.
      //
      // Known risk, accepted: gtag batches, and window.location.reload() is two
      // lines away, so this beacon can be dropped in flight. Firing it after
      // the reload is not an option — the reload is what ends this page — and
      // moving it earlier would report a switch that had not happened yet.
      // `workspace_selected` is therefore a slight undercount by design.
      // (SubscriberSelect's workspace branch has the same call and no reload,
      // so it is unaffected.)
      setAnalyticsUserContext({
        subscriberId: subscriber.id,
        subscriberName: subscriber.name,
        role: subscriber.role,
      });
      trackEvent('workspace_selected');

      // Navigate to tasks page and reload to refresh all data for new subscriber
      navigate('/tasks');
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch subscriber:', err);
      setSwitching(false);
    }
  };

  const handleCustomerSwitch = async (customer) => {
    // Check if already viewing this customer portal
    if (userRole === 'customer' && customerInfo?.customerId === customer.id) {
      handleMenuClose();
      return;
    }

    setSwitching(true);
    handleMenuClose();
    try {
      // No analytics here, deliberately — the same call the workspace branch
      // above makes would be wrong twice over. This path ends in a full page
      // reload, which throws gtag's state away, and AuthContext then sets the
      // whole context from the fresh customer token on boot; a call here would
      // be overwritten by the same values moments later. And
      // `workspace_selected` would misname it: a customer portal is not a
      // workspace. Same reasoning as SubscriberSelect's customer branch.
      await setCustomClaims(customer.subscriberId, customer.id);
      const { auth: firebaseAuth } = await import('../firebase');
      if (firebaseAuth.currentUser) {
        await firebaseAuth.currentUser.getIdToken(true);
      }
      navigate('/customer-portal');
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch to customer portal:', err);
      setSwitching(false);
    }
  };

  const handleSystemAdminClick = () => {
    handleMenuClose();
    navigate('/system-admin');
  };

  // Not ticked while in System Admin: that entry is ticked instead, and two
  // ticks read as being in both places at once.
  const isCurrentSubscriber = (sub) => {
    return !inSystemAdmin && userRole !== 'customer' && sub.id === subscriberId;
  };

  const isCurrentCustomer = (cust) => {
    return userRole === 'customer' && customerInfo?.customerId === cust.id;
  };

  return (
    <>
      <Button
        color="inherit"
        onClick={handleMenuOpen}
        disabled={switching}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: '7px',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
        }}
      >
        {workspaceLabel(true)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: 250,
              mt: 1
            }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Switch Workspace
          </Typography>
        </Box>
        <Divider />

        {isSystemAdmin && (
          <>
            <MenuItem
              onClick={handleSystemAdminClick}
              selected={inSystemAdmin}
              sx={{
                backgroundColor: 'rgba(21, 138, 71, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(21, 138, 71, 0.12)'
                }
              }}
            >
              <ListItemIcon>
                {inSystemAdmin
                  ? <CheckIcon fontSize="small" color="primary" />
                  : <SystemAdminIcon fontSize="small" color="primary" />}
              </ListItemIcon>
              <ListItemText
                primary="System Admin"
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: 'primary.main'
                }}
              />
            </MenuItem>
            <Divider />
          </>
        )}

        {/* Subscriber entries */}
        {subscribers.length > 0 && (
          <>
            {customers.length > 0 && (
              <Box sx={{ px: 2, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Subscribers
                </Typography>
              </Box>
            )}
            {subscribers.map((subscriber) => (
              <MenuItem
                key={subscriber.id}
                onClick={() => handleSubscriberSwitch(subscriber)}
                selected={isCurrentSubscriber(subscriber)}
              >
                <ListItemIcon>
                  {isCurrentSubscriber(subscriber) ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : (
                    <PersonIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={subscriber.name || subscriber.subscriberCode}
                  secondary={subscriber.role}
                  primaryTypographyProps={{
                    fontWeight: isCurrentSubscriber(subscriber) ? 600 : 400
                  }}
                />
              </MenuItem>
            ))}
          </>
        )}

        {/* Customer portal entries */}
        {customers.length > 0 && (
          <>
            {subscribers.length > 0 && <Divider />}
            <Box sx={{ px: 2, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Customer Portals
              </Typography>
            </Box>
            {customers.map((customer) => (
              <MenuItem
                key={`customer-${customer.id}`}
                onClick={() => handleCustomerSwitch(customer)}
                selected={isCurrentCustomer(customer)}
              >
                <ListItemIcon>
                  {isCurrentCustomer(customer) ? (
                    <CheckIcon fontSize="small" color="secondary" />
                  ) : (
                    <CustomerIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={customer.name}
                  secondary="Customer Portal"
                  primaryTypographyProps={{
                    fontWeight: isCurrentCustomer(customer) ? 600 : 400
                  }}
                />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
}

export default SubscriberSwitcher;
